import React, { useEffect, useState } from "react";
import styled from 'styled-components';
import { BsArrowsAngleExpand } from "react-icons/bs";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  AspectRatio,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels
} from "@chakra-ui/react";

import { Card } from "components";
import { joinSession } from "api/webservice/sessions";
import { useUser } from "context/user";
import useSelectedParticipant from "context/session/VideoProvider/useSelectedParticipant";
import {
  useChatContext,
  useParticipants,
  useVideoContext,
  useLocalAudioToggle,
  useLocalVideoToggle
} from "hooks/session";
import { useToast } from "hooks";
import {
  AudioButton,
  ChatButton,
  EndCallButton,
  ScreenShareButton,
  VideoButton
} from "./Buttons";
import Chat from "./Chat";
import ParticipantList from "./ParticipantList";
import GameLiftStreamsElement from "./GameLiftStreams";
import { Session } from "types";
import CallOverlay from "./CallOverlay";
import useGameLiftStreams from "hooks/session/useGameLiftStreams";
import VoiceMorph from "./Voice";

const ResponsiveGrid = styled.div`
    width: min(140vh, 65vw);
    padding-bottom: 24px;
    padding-left: 4px;
    padding-right: 4px;
    grid-template-columns: auto;
    grid-template-areas: 'heading' 'video' 'chat';

    @media screen and (min-width: 1100px) {
      padding-left: 16px;
      padding-right: 16px;
    }

    display: grid;
    grid-template-columns: auto;
    grid-template-rows: auto;

    @media screen and (min-width: 1100px) {
      grid-template-columns: 2fr 1fr;
      grid-template-rows: min-content minmax(60vh, 100%);
    }

    @media screen and (min-width: 1440px) {
      grid-template-columns: 3fr 1fr;
    }

    @media screen and (min-width: 1920px) {
      grid-template-columns: 5fr 1fr;
    }

    grid-template-areas: 'heading' 'video' 'chat';

    @media screen and (min-width: 1100px) {
      grid-template-areas: 'heading chat' 'video chat';
    }

    column-gap: 0;
    row-gap: 8px;

    @media screen and (min-width: 1100px) {
      column-gap: 8px;
      row-gap: 0;
    }

    @media screen and (min-width: 1100px) {
      column-gap: 12px;
    }

    @media screen and (min-width: 1920px) {
      column-gap: 24px;
    }
  `;

interface Props {
  session: Session;
}

const Room: React.FC<Props> = ({ session }: Props) => {
  const { user } = useUser()!;
  const {
    connect: videoConnect,
    room,
    getAudioAndVideoTracks
  } = useVideoContext();
  const {
    connect: chatConnect,
    isChatWindowOpen,
    setIsChatWindowOpen
  } = useChatContext();
  const [
    selectedParticipant,
    setSelectedParticipant
  ] = useSelectedParticipant();
  const participants = useParticipants();
  const {
    gameConnected,
    gameStarted,
    gameLoading,
    gameFocused,
    gameLiftStreamsInput,
    gameState,
    errorOccurred,
    retried,
    toggleGameConnected,
    toggleGameStarted,
    toggleGameLiftStreamsInput,
    toggleGameFocus,
    initGameLiftStreams,
    gameDisconnect,
  } = useGameLiftStreams(session.id);

  const element = document.getElementById("video");
  const [chatHeight, setChatHeight] = useState(0);
  const [zIndex, setZIndex] = useState(-9999);
  const [gameLiftStreamsWidth, setGameLiftStreamsWidth] = useState("0%");
  const [participantsListWidth, setParticipantsListWidth] = useState("100%")

  const trademarkIcon = "\u2122";

  const handleResize = () => {
    const videoHeight = document.getElementById("video");
    if (videoHeight) {
      setChatHeight(videoHeight.clientHeight);
    } else {
      //set a default height in case of errors
      setChatHeight(300);
    };
  };

  const toast = useToast();

  const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();

  const reconnect = (): void => {
    getAudioAndVideoTracks();
    joinSession(session.id)
      .then(async token => {
        await videoConnect(token);
        await chatConnect(token);
      })
      .catch(error => {
        toast({
          title: "Error joining session",
          description: error.response.data.response,
          status: "error"
        });
      });
  };


  /*   const toggleVideo = () => {
      if (isVideoEnabled) {
       toggleVideoEnabled();
        return () => {toggleVideoEnabled();};
      }
      else return () => {};
    } */

  const toggleAudio = () => {
    if (isAudioEnabled) {
      toggleAudioEnabled();
      return toggleAudioEnabled;
    }
    else return () => { };
  }

  // connect to GameLiftStreams stream
  const connectToStream = (): void => {
    if (!user || !user.id) return;

    if (gameStarted && !gameFocused) {   	
      changeFocus(null);
    } else if (gameStarted && gameFocused) {
      // don't need to do anything, this is for if someone presses the Charisma button after the game's started and visible on main screen
    } else { 
      toast({
        title: "Loading...",
        description: "Please wait as Charisma loads...",
        status: "info",
        duration: 20000 //keep it visible for 20 seconds, from what ive seen if it's not loaded by then the stream is likely to error out
      });
      closeToast("gameLoadedToast");
      /* const toggleVideoCallback = toggleVideo(); */
      const toggleAudioCallback = toggleAudio();
      /* initGameLiftStreams(); */
      sessionStorage.setItem('gameStarted', 'true');
      toggleGameStarted();
      toggleZIndex();
      setTimeout(() => {
        /* toggleVideoCallback(); */
        toggleAudioCallback();
        // move toggleWidth here since I think if participantList width gets adjusted before the game connects,
        // the "waiting for client" message is visible on the left side of the video area
        toggleWidth();
      }, 3000);
    }
  };

  const toggleZIndex = (): void => {
    setZIndex(-zIndex);
  }
   	
  const toggleWidth = (): void => {
    setGameLiftStreamsWidth(gameLiftStreamsWidth === "0%" ? "100%" : "0%");
    setParticipantsListWidth(participantsListWidth === "0%" ? "100%" : "0%");
  }
  
  const changeFocus = (participant: any): void => {
    try {
      if (gameFocused) {
        toggleGameFocus();
        toggleWidth();
        if (participant) {
          setSelectedParticipant(participant); // focus on the participant
          console.log("focusing on a specific participant, game unfocusing")
        }
        else {
          // this case should never happen... but just in case it does.
          if (selectedParticipant) {
            setSelectedParticipant(selectedParticipant);
          }
          console.log("focusing on no specific participant, game unfocusing")
        }
      }
      else {                                     // game not focused
        if (participant) {
          setSelectedParticipant(participant);
          console.log("focusing on a specific participant, game already unfocused")
        }
        else {                                   // refocus the game
          toggleGameFocus();
          toggleWidth();
          if (selectedParticipant) {
            setSelectedParticipant(selectedParticipant);
          }
          console.log("refocusing on the game, game already unfocused")
        }
      }
    }
    catch (e) {
      toast({
        title: "Slow Down",
        description: "Please wait a moment before doing that.",
        status: "info"
      })
      console.log(e);
    }
  };

    //use a setTimeout for now to give things a chance to load in, might not be the best way of dealing with this
    setTimeout(handleResize, 500);

  // toggle fullscreen
  function toggleFullscreen() {
    if (!document.fullscreenElement && element) {
      element.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  function closeToast(toastId: string) {
    toast.close(toastId);
  };

  //start the stream connection when the room is rendered
  useEffect(() => { 	
    if (gameState=="connecting") {
      setTimeout(() => {
        initGameLiftStreams();
      }, 3000);
      //give it some time to set up the stream before connecting
      setTimeout(() => {
        toggleGameConnected();
        // for some reason, the toggling between participant video and minimized game wont work, unless
        // gameFocus is true when initializing gameliftstreams element
        toggleGameFocus();
      }, 3000);
    } else if (gameState=="connected") {
      //Only show toast if user hasnt already pressed the Charisma button
      if (gameStarted === false) {
        // give it a few seconds for game autologin attempt
        setTimeout(() => {
          toast({
            id: "gameLoadedToast",
            title: "Charisma has loaded",
            description: "Press the button to launch Charisma.",
            status: "info",
            duration: null
          });
        }, 3000)
      }
    }
  }, [gameState]);
   	
  // hide the participants list if the game disconnects to avoid "Waiting for Client" text on the edges
  // and if there are no participants in the room
  useEffect(() => {
    if (errorOccurred && participants.length == 0) {
      if (participantsListWidth == "0%") {
        toggleWidth();
      }
    }
  }, [errorOccurred]);

  // reconnect when the browser refreshes
  useEffect(() => {
    if (!room) {
      reconnect();
    }
    setIsChatWindowOpen(true);
    gameDisconnect();
    if (gameFocused) {
      toggleGameFocus();
    }
    const storedSessionId = sessionStorage.getItem('session.id');
    const storedGameState = sessionStorage.getItem('gameStarted');
    if (storedSessionId && storedGameState &&
      storedSessionId == session.id && storedGameState == 'true') {
      initGameLiftStreams();
    }

    return () => {
      room?.disconnect();
    };
  }, [room]);

  //set the event listener once
  useEffect(() => {
    //call the function initially to see if it can set the height, may help during refreshes but not sure
    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);
    
  if (!room) return null;

  return (
    <Flex
      justify="center" >
      <ResponsiveGrid>
        <GridItem gridArea="heading" py={8}>
          <Text fontSize="2xl">&quot;{session?.title}&quot;</Text>
        </GridItem>
        <HStack gridArea="heading" w="100%" justifyContent="flex-end" py={8}>
          <EndCallButton session={session} />
        </HStack>
        <GridItem gridArea="video" h="100%">
          <VStack spacing={5} h="100%" w="100">
            <Card h="auto" maxWidth="1954px">
              <AspectRatio ratio={16 / 9}>
                <div id="video" style={{ maxWidth: "100%" }} onClick={() => toggleGameLiftStreamsInput(true)}>
                  <Flex borderRadius="17px" h="100%" w="100%">
                    {(gameConnected && gameFocused && (gameState !== 'disconnected' || gameState !== 'failed' || gameState !== 'closed')) &&
                    <Box width={gameLiftStreamsWidth}>
                      {<GameLiftStreamsElement sessionId={session.id} />}
                    </Box>}
                    {(!gameStarted || !gameFocused) &&
                    <Box width={participantsListWidth}>
                      <ParticipantList gameStarted={gameStarted}/>
                    </Box>}
                    {gameStarted && gameFocused && !gameLiftStreamsInput &&
                     <Box
                       position={'absolute'}
                       top={0}
                       left={0}
                       right={0}
                       bottom={0}
                       z-index={9999}
                       onClick={() => toggleGameLiftStreamsInput(true)}
                     />}
                    <CallOverlay
                      changeFocus={changeFocus}
                      sessionId={session.id}
                    />
                  </Flex>
                </div>
              </AspectRatio>
            </Card>
            <ButtonGroup size="xl" variant="outline" spacing={6}>
              <AudioButton />
              <VideoButton />
              <Button
                rounded="xl"
                variant="blue"
                onClick={connectToStream}
                isLoading={gameLoading}
              >
                <Text>CHARISMA</Text>
                <Flex h="100%" alignSelf="flex-end">
                  <Text fontSize="10px">{trademarkIcon}</Text>
                </Flex>
              </Button>
              <IconButton
                aria-label="Toggle Fullscreen"
                onClick={toggleFullscreen}
                icon={<BsArrowsAngleExpand />}
              />
              <ScreenShareButton />
              <ChatButton />
            </ButtonGroup>
          </VStack>
        </GridItem>
        <GridItem
          gridArea="chat"
          className="chat-container"
          h="100%"
          w="375px"
          py={8}
          /* overflow="scroll" */
          maxHeight="35vw"
          maxWidth="70vw"
          /* sx={{
            '::-webkit-scrollbar': {
              display: 'none'
            }
          }} */
        >
          <Tabs isFitted variant="unstyled">
            <TabList
              color="gray.100"
              bg="gray.700"
              borderRadius={"10px"}
            >
              <Tab _selected={{ color: "gray.900", bg: "blue.500", borderRadius: "10px" }} _focus={{ bg: "transparent" }}>Chat</Tab>
              <Tab _selected={{ color: "gray.900", bg: "blue.500", borderRadius: "10px" }} _focus={{ bg: "transparent" }}>Audio</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                {isChatWindowOpen && <Chat toggleGameLiftStreamsChatInput={toggleGameLiftStreamsInput} componentHeight={chatHeight + 87}/>}
              </TabPanel>
              <TabPanel>
                <VoiceMorph toggleGameLiftStreamsInput={toggleGameLiftStreamsInput} componentHeight={chatHeight + 87}/>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
      </ResponsiveGrid>
    </Flex>
  );
};

export default Room;
